import { log } from './production-logger';
import {
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  onSnapshot, 
  doc, 
  updateDoc, 
  addDoc, 
  deleteDoc,
  writeBatch,
  getDocs,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { firestore as db } from '../firebase/firebase';
import { 
  Notification, 
  NotificationType, 
  NotificationData, 
  NotificationPriority, 
  NotificationCategory,
  NotificationFilter,
  NotificationStats
} from '../types/notification';

export class NotificationService {
  private static readonly COLLECTION = 'notifications';

  /**
   * Create a new notification
   */
  static async createNotification(
    organizationId: string,
    userId: string,
    type: NotificationType,
    title: string,
    message: string,
    data?: NotificationData,
    priority: NotificationPriority = 'medium'
  ): Promise<string> {
    try {
      const category = this.getCategoryFromType(type);
      
      const notification: Omit<Notification, 'id' | 'createdAt'> = {
        type,
        title,
        message,
        data,
        read: false,
        organizationId,
        userId,
        priority,
        category,
      };

      const docRef = await addDoc(collection(db, this.COLLECTION), {
        ...notification,
        createdAt: serverTimestamp(),
      });

      return docRef.id;
    } catch (error) {
      log.error('Error creating notification:', error);
      throw error;
    }
  }

  /**
   * Get notifications for a user with real-time updates
   */
  static subscribeToNotifications(
    organizationId: string,
    userId: string,
    callback: (notifications: Notification[]) => void,
    filter?: NotificationFilter,
    limitCount: number = 50
  ) {
    try {
      let q = query(
        collection(db, this.COLLECTION),
        where('organizationId', '==', organizationId),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      // Apply filters
      if (filter?.category) {
        q = query(q, where('category', '==', filter.category));
      }
      if (filter?.type) {
        q = query(q, where('type', '==', filter.type));
      }
      if (filter?.priority) {
        q = query(q, where('priority', '==', filter.priority));
      }
      if (filter?.read !== undefined) {
        q = query(q, where('read', '==', filter.read));
      }

      return onSnapshot(q, (snapshot) => {
        const notifications: Notification[] = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          } as Notification;
        });

        callback(notifications);
      });
    } catch (error) {
      log.error('Error subscribing to notifications:', error);
      throw error;
    }
  }

  /**
   * Mark notification as read
   */
  static async markAsRead(notificationId: string): Promise<void> {
    try {
      const notificationRef = doc(db, this.COLLECTION, notificationId);
      await updateDoc(notificationRef, {
        read: true,
      });
    } catch (error) {
      log.error('Error marking notification as read:', error);
      throw error;
    }
  }

  /**
   * Mark multiple notifications as read
   */
  static async markMultipleAsRead(notificationIds: string[]): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      notificationIds.forEach(id => {
        const notificationRef = doc(db, this.COLLECTION, id);
        batch.update(notificationRef, { read: true });
      });

      await batch.commit();
    } catch (error) {
      log.error('Error marking multiple notifications as read:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read for a user
   */
  static async markAllAsRead(organizationId: string, userId: string): Promise<void> {
    try {
      const q = query(
        collection(db, this.COLLECTION),
        where('organizationId', '==', organizationId),
        where('userId', '==', userId),
        where('read', '==', false)
      );

      const snapshot = await getDocs(q);
      const batch = writeBatch(db);

      snapshot.docs.forEach(doc => {
        batch.update(doc.ref, { read: true });
      });

      await batch.commit();
    } catch (error) {
      log.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  /**
   * Delete a notification
   */
  static async deleteNotification(notificationId: string): Promise<void> {
    try {
      const notificationRef = doc(db, this.COLLECTION, notificationId);
      await deleteDoc(notificationRef);
    } catch (error) {
      log.error('Error deleting notification:', error);
      throw error;
    }
  }

  /**
   * Delete all read notifications for a user
   */
  static async deleteReadNotifications(organizationId: string, userId: string): Promise<void> {
    try {
      const q = query(
        collection(db, this.COLLECTION),
        where('organizationId', '==', organizationId),
        where('userId', '==', userId),
        where('read', '==', true)
      );

      const snapshot = await getDocs(q);
      const batch = writeBatch(db);

      snapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      await batch.commit();
    } catch (error) {
      log.error('Error deleting read notifications:', error);
      throw error;
    }
  }

  /**
   * Get notification statistics
   */
  static async getNotificationStats(organizationId: string, userId: string): Promise<NotificationStats> {
    try {
      const q = query(
        collection(db, this.COLLECTION),
        where('organizationId', '==', organizationId),
        where('userId', '==', userId)
      );

      const snapshot = await getDocs(q);
      const notifications = snapshot.docs.map(doc => doc.data() as Notification);

      const stats: NotificationStats = {
        total: notifications.length,
        unread: notifications.filter(n => !n.read).length,
        byCategory: {
          invoice: 0,
          supplier: 0,
          payment: 0,
          subscription: 0,
          organization: 0,
          system: 0,
          security: 0,
        },
        byPriority: {
          low: 0,
          medium: 0,
          high: 0,
          urgent: 0,
        },
      };

      notifications.forEach(notification => {
        stats.byCategory[notification.category]++;
        stats.byPriority[notification.priority]++;
      });

      return stats;
    } catch (error) {
      log.error('Error getting notification stats:', error);
      throw error;
    }
  }

  /**
   * Helper method to determine category from notification type
   */
  private static getCategoryFromType(type: NotificationType): NotificationCategory {
    const categoryMap: Record<NotificationType, NotificationCategory> = {
      invoice_created: 'invoice',
      invoice_updated: 'invoice',
      invoice_paid: 'invoice',
      invoice_overdue: 'invoice',
      supplier_added: 'supplier',
      supplier_updated: 'supplier',
      payment_received: 'payment',
      payment_failed: 'payment',
      subscription_renewal: 'subscription',
      subscription_expired: 'subscription',
      member_added: 'organization',
      member_removed: 'organization',
      organization_updated: 'organization',
      system_update: 'system',
      security_alert: 'security',
    };

    return categoryMap[type] || 'system';
  }

  /**
   * Helper methods for creating specific notification types
   */
  static async createInvoiceNotification(
    organizationId: string,
    userId: string,
    type: 'invoice_created' | 'invoice_updated' | 'invoice_paid' | 'invoice_overdue',
    invoiceId: string,
    supplierName?: string,
    amount?: number,
    currency?: string
  ) {
    const titles = {
      invoice_created: 'New Invoice Added',
      invoice_updated: 'Invoice Updated',
      invoice_paid: 'Invoice Paid',
      invoice_overdue: 'Invoice Overdue',
    };

    const messages = {
      invoice_created: `A new invoice from ${supplierName || 'a supplier'} has been added`,
      invoice_updated: `Invoice from ${supplierName || 'a supplier'} has been updated`,
      invoice_paid: `Payment received for invoice from ${supplierName || 'a supplier'}`,
      invoice_overdue: `Invoice from ${supplierName || 'a supplier'} is now overdue`,
    };

    const priority: NotificationPriority = type === 'invoice_overdue' ? 'high' : 'medium';

    return this.createNotification(
      organizationId,
      userId,
      type,
      titles[type],
      messages[type],
      {
        invoiceId,
        amount,
        currency,
        targetScreen: 'InvoiceDetail',
        targetParams: { invoiceId },
      },
      priority
    );
  }

  static async createSupplierNotification(
    organizationId: string,
    userId: string,
    type: 'supplier_added' | 'supplier_updated',
    supplierId: string,
    supplierName: string
  ) {
    const titles = {
      supplier_added: 'New Supplier Added',
      supplier_updated: 'Supplier Updated',
    };

    const messages = {
      supplier_added: `${supplierName} has been added to your suppliers`,
      supplier_updated: `${supplierName} details have been updated`,
    };

    return this.createNotification(
      organizationId,
      userId,
      type,
      titles[type],
      messages[type],
      {
        supplierId,
        targetScreen: 'SupplierDetail',
        targetParams: { supplierId },
      }
    );
  }

  static async createPaymentNotification(
    organizationId: string,
    userId: string,
    type: 'payment_received' | 'payment_failed',
    amount: number,
    currency: string,
    invoiceId?: string
  ) {
    const titles = {
      payment_received: 'Payment Received',
      payment_failed: 'Payment Failed',
    };

    const messages = {
      payment_received: `Payment of ${amount} ${currency} has been received`,
      payment_failed: `Payment of ${amount} ${currency} has failed`,
    };

    const priority: NotificationPriority = type === 'payment_failed' ? 'high' : 'medium';

    return this.createNotification(
      organizationId,
      userId,
      type,
      titles[type],
      messages[type],
      {
        amount,
        currency,
        invoiceId,
        targetScreen: invoiceId ? 'InvoiceDetail' : 'Dashboard',
        targetParams: invoiceId ? { invoiceId } : {},
      },
      priority
    );
  }
}

// Export as default as well for backward compatibility
export default NotificationService;
